import {computed, observable, override} from "mobx";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {BookmakerViewModel} from "../../../domain/common/bookmaker/models/bookmaker-view-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {COMPETITIONS_SLIDER_WIDGET} from "../competition-slider-widget.consts"
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {CompetitionsSliderWidgetConfig} from "../competitions-slider-widget.config";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {CompetitionsSliderCompetitionViewModel} from "./competitions-slider.competition-view-model";
import {BookmakerDomainModel} from "../../../domain/common/bookmaker/models/bookmaker-domain-model";
import {GamesSliderWidgetState} from "../../games-slider-widget/models/games-slider-widget.state";
import {UserAgentViewModel} from "../../../core/models/user-agent.view-model";

export class CompetitionsSliderWidgetState extends GamesSliderWidgetState {
  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, CompetitionsSliderWidgetConfig, UserAgentViewModel];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: CompetitionsSliderWidgetConfig,
    userAgent: UserAgentViewModel,
  ) {
    super(
      navigationManager,
      appStore,
      logger,
      datetimeUtils,
      device,
      termsUtils,
      config
    );

    this.userAgent = userAgent;
  }

  @observable selectedCompetitionId: Number = -1;
  @observable competitionIds: Array<Number> = [];
  @observable competitionsDataSource: Array<CompetitionDomainModel> = [];

  @computed get competitions(): Array<CompetitionViewModel> {
    const competitions: Array<CompetitionViewModel> = [];

    this.competitionsDataSource.forEach((competitionDomainModel: CompetitionDomainModel) => {
      const competitionViewModel: CompetitionsSliderCompetitionViewModel = new CompetitionsSliderCompetitionViewModel(competitionDomainModel);

      if (competitionViewModel.id === COMPETITIONS_SLIDER_WIDGET.DEFAULT_COMPETITION_TOP_GAMES_ID) {
        competitionViewModel.sortIndex = -1;
      } else {
        competitionViewModel.sortIndex = 0;
      }

      competitions.push(competitionViewModel);
    });

    this.props.competitionIds?.forEach((id: Number, index: Number) => {
      const competition = competitions?.find((competitionViewModel: CompetitionsSliderCompetitionViewModel) => competitionViewModel.id === id);

      if (competition) {
        competition.sortIndex = index;
      }
    })

    competitions?.sortAscending('sortIndex');

    return competitions;
  }

  @computed get selectedCompetitionsIds(): Array<Number> {
    return this.isTopGames ? this.competitions.map((competitionViewModel: CompetitionsSliderCompetitionViewModel) => competitionViewModel.id) : [this.selectedCompetitionId];
  }

  @computed get selectedCompetition(): Array<Number> {
    const selectedCompetition: CompetitionViewModel = this.competitions.find((competition: CompetitionsSliderCompetitionViewModel) => competition.id === this.selectedCompetitionId);

    return selectedCompetition
  }

  @override get isPopulated(): Boolean {
    return super.isPopulated && this.selectedCompetitionId !== 0;
  }

  @computed get isTopGames(): Array<Number> {
    return this.selectedCompetitionId === COMPETITIONS_SLIDER_WIDGET.DEFAULT_COMPETITION_TOP_GAMES_ID;
  }

  @computed get isLeftBarVisible() {
    return this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm;
  }

  @computed get isRightBarVisible() {
    const isVisibleByWidth = this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm;

    return isVisibleByWidth && (this.sponsoredBookmakerVisible || this.shouldRenderPoweredBy);
  }

  @computed get isTopBarVisible(): Boolean {
    const isVisibleByWidth: Boolean = this.device.screenWidth < COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm

    return isVisibleByWidth && (this.shouldRenderPoweredBy || this.sponsoredBookmakerVisible || this.shouldRenderTopDropdown)
  }

  @computed get shouldRenderTopDropdown() {
    const isVisibleByWidth = this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.xxs
      && this.device.screenWidth < COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm;

    return isVisibleByWidth;
  }

  @computed get isMobile(): Boolean {
    return this.device.screenWidth < COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.xxs;
  }

  @computed get allScoresUrl(): BaseLinkModel {
    return this.appSettings.publisherSettings?.allScoresUrl;
  }

  @computed get containerClassName(): String {
    let size: String = COMPETITIONS_SLIDER_WIDGET.DEVICES.mobile;

    if ((this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.xxs) && (this.device.screenWidth > COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.xs)) {
      size = COMPETITIONS_SLIDER_WIDGET.DEVICES.tablet
    } else if ((this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.xs) && (this.device.screenWidth > COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm)) {
      size = COMPETITIONS_SLIDER_WIDGET.DEVICES.desktop
    } else if (this.device.screenWidth >= COMPETITIONS_SLIDER_WIDGET.SCREEN_RESOLUTIONS.sm) {
      size = COMPETITIONS_SLIDER_WIDGET.DEVICES.largeDesktop
    }

    const className: String = `competitions-slider-widget-${size}`;

    return className;
  }

  @computed get sponsoredBookmakerVisible(): Boolean {
    return this.appSettings.bootstrapSettings.showSponsorship;
  }

  @override get sponsorshipBookmaker(): BookmakerViewModel {
    const bookmakerDomainModel: BookmakerDomainModel = this.dataSource?.bookmakers?.first();
    const bookmakerViewModel: BookmakerViewModel = bookmakerDomainModel && new BookmakerViewModel(bookmakerDomainModel);

    return bookmakerViewModel;
  }

  @computed get emptyStateText(): String {
    return (!this.games || this.games.length === 0) ? this.terms.SLIDER_WIDGET_EMPTY_STATE : null;
  }

  @computed get shouldRenderPoweredBy(): Boolean {
    return this.appState.shouldRenderSponsorshipPoweredBy
  }
}
